<script setup>
import { computed } from 'vue';
import { intlLocale, t } from '../i18n';
import { plural } from '../operations';
import { formatDay, formatLevel, formatRub } from '../achievements';

// One habit of «Достижения» in a sheet (docs/achievements/SPEC.md, «Проект v2»): the rule in one phrase, the
// number now, and its levels — reached with the date, the next one ahead.
const props = defineProps({
  // 'streak' | 'income' | 'drawdown' | 'diversity'
  kind: {
    type: String,
    required: true,
  },
  habits: {
    type: Object,
    required: true,
  },
});

// Levels as on the server (App\Services\Achievements\Achievements).
const LADDERS = {
  streak: [3, 6, 12, 24, 36, 60],
  income: [500, 1000, 2500, 5000, 10000, 25000, 50000, 100000],
  drawdown: [10, 20, 30],
  diversity: [3, 5, 8, 10],
};

function percent(share) {
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1 }).format(share * 100);
}

function levelLabel(level) {
  switch (props.kind) {
    case 'streak':
      return t('achievements.monthsShort', { count: level });
    case 'income':
      return t('achievements.perMonth', { sum: formatRub(level) });
    case 'drawdown':
      return `−${level} %`;
    default:
      return plural(level, 'achievements.sectorsCount');
  }
}

const habit = computed(() => (props.kind === 'diversity' ? props.habits.diversity?.sectors : props.habits[props.kind]));

// The number now, to say how far the next level is and which reached ones are past bests.
const now = computed(() => {
  const h = props.habits;
  return {
    streak: h.streak.current,
    income: h.income.per_month,
    drawdown: null,
    diversity: h.diversity?.sectors.count ?? 0,
  }[props.kind];
});

function aheadText(level) {
  if (now.value === null) {
    return t('achievements.ahead');
  }
  const left = Math.max(0, level - now.value);
  const amount = {
    streak: () => t('achievements.monthsShort', { count: left }),
    income: () => t('achievements.perMonth', { sum: formatRub(left) }),
    diversity: () => plural(left, 'achievements.sectorsCount'),
  }[props.kind]();
  return t('achievements.aheadLeft', { left: amount });
}

function pastBest(level) {
  return props.kind === 'diversity' && now.value !== null && level > now.value;
}

// Reached levels with dates, then the next one; the rest are not shown.
const levels = computed(() => {
  const reached = new Map((habit.value?.levels ?? []).map((l) => [l.level, l.date]));
  const out = [];
  for (const level of LADDERS[props.kind]) {
    if (reached.has(level)) {
      out.push({ level, date: reached.get(level) });
    } else {
      out.push({ level, date: null });
      break;
    }
  }
  return out.reverse();
});

const headline = computed(() => {
  const h = props.habits;
  switch (props.kind) {
    case 'streak':
      return {
        value: t('achievements.monthsShort', { count: h.streak.current }),
        note: h.streak.current > 0 && h.streak.current === h.streak.best ? t('achievements.streakIsBest') : t('achievements.streakBest', { best: h.streak.best }),
      };
    case 'income':
      return {
        value: t('achievements.perMonth', { sum: formatRub(h.income.per_month) }),
        note: t('achievements.incomeTotals', { year: formatRub(h.income.last12), total: formatRub(h.income.total) }),
      };
    case 'drawdown':
      return {
        value: h.drawdown.ongoing ? t('achievements.drawdownNowShort', { depth: percent(h.drawdown.ongoing.depth) }) : '',
        note: h.drawdown.ongoing
          ? t('achievements.drawdownNow', { depth: percent(h.drawdown.ongoing.depth), sum: formatLevel(Math.max(0, h.drawdown.ongoing.invested)) })
          : '',
      };
    default:
      return {
        value: h.diversity ? plural(h.diversity.sectors.count, 'achievements.sectorsCount') : '',
        note: [
          h.diversity?.largest ? t('achievements.largestShare', { ticker: h.diversity.largest.ticker ?? '—', share: percent(h.diversity.largest.share) }) : '',
          h.diversity?.max10_since ? t('achievements.max10Since', { date: formatDay(h.diversity.max10_since) }) : t('achievements.max10Not'),
        ].filter(Boolean).join(' · '),
      };
  }
});
</script>

<template>
  <div>
    <p class="text-[0.8125rem] text-slate-400">
      {{ t(`achievements.rule.${kind}`) }}
    </p>
    <p
      v-if="headline.value"
      class="mt-3 text-xl font-semibold tabular-nums text-slate-100"
    >
      {{ headline.value }}
    </p>
    <p
      v-if="headline.note"
      class="mt-0.5 text-[0.8125rem] text-slate-400"
    >
      {{ headline.note }}
    </p>

    <!-- Drawdowns lived through: each with its dates -->
    <ul
      v-if="kind === 'drawdown' && habits.drawdown.episodes.length > 0"
      class="mt-3 divide-y divide-slate-800"
    >
      <li
        v-for="episode in [...habits.drawdown.episodes].reverse()"
        :key="episode.peak"
        class="flex min-h-12 items-center justify-between gap-3 py-2"
      >
        <span class="text-[0.9375rem] tabular-nums text-slate-100 sm:text-sm">−{{ percent(episode.depth) }} %</span>
        <span class="text-right text-[0.8125rem] text-slate-400">
          {{ t('achievements.drawdownDates', { from: formatDay(episode.peak), to: formatDay(episode.recovered_at) }) }}
        </span>
      </li>
    </ul>

    <h4 class="mt-4 text-[0.8125rem] font-medium text-slate-500">
      {{ t('achievements.levels') }}
    </h4>
    <ul class="divide-y divide-slate-800">
      <li
        v-for="level in levels"
        :key="level.level"
        class="flex min-h-12 items-center justify-between gap-3 py-2"
      >
        <span
          class="text-[0.9375rem] tabular-nums sm:text-sm"
          :class="level.date ? 'text-slate-100' : 'text-slate-500'"
        >{{ levelLabel(level.level) }}</span>
        <span
          class="text-[0.8125rem]"
          :class="level.date ? 'text-slate-300' : 'text-slate-500'"
        >{{ level.date ? formatDay(level.date) : aheadText(level.level) }}<template v-if="level.date && pastBest(level.level)"> · {{ t('achievements.best') }}</template></span>
      </li>
    </ul>
  </div>
</template>
